import { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ShippingOrder, ShippingLineItem } from '../DeShippingTableWrapper';
import { transformLineItemChangesToApiFormat, LineItemChanges } from '../utils/lineItemTransforms';

interface ChangesSummary {
  lineItemId: string;
  productName: string;
  productSku: string;
  changes: {
    field: string;
    fieldLabel: string;
    oldValue: string;
    newValue: string;
  }[];
}

interface UseAdminControlDialogProps {
    order?: ShippingOrder;
    lineItems: ShippingLineItem[];
    updateLineItemsBulk: (orderId: string, updates: any) => Promise<any>;
    refetch: () => void;
    updateSingleOrder: (orderId: string) => Promise<any>;
  }
export const useAdminControlDialog = ({
  order,
  lineItems,
  updateLineItemsBulk,
  refetch,
  updateSingleOrder
}: UseAdminControlDialogProps) => {
  const [lineItemChanges, setLineItemChanges] = useState<LineItemChanges>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [changesSummary, setChangesSummary] = useState<ChangesSummary[]>([]);
  const toastRef = useRef<Toast>(null);

  // Clear save error when user makes new changes
  useEffect(() => {
    if (Object.keys(lineItemChanges).length > 0 && saveError) {
      setSaveError(null);
    }
  }, [lineItemChanges, saveError]);

  const fulfillmentStatusOptions = [
    { label: 'Unassigned', value: 'unassigned' },
    { label: 'Assigned', value: 'assigned' },
    { label: 'Store Declined Order', value: 'store_declined_order' },
    { label: 'Accepted, Awaiting Inspection Approval', value: 'accepted_awaiting_inspection' },
    { label: 'Rejected by Admin', value: 'rejected_by_admin' },
    { label: 'Awaiting Shipment', value: 'awaiting_shipment' },
    { label: 'Shipped', value: 'shipped' },
    { label: 'Fulfilled', value: 'fulfilled' }
  ];

  const fulfillmentLocationOptions = [
    {label: 'R001 - POV Sawgrass', value: 'R001 - POV Sawgrass'},
    {label: 'R002 - Just One Look', value: 'R002 - Just One Look'},
    {label: 'R003 - Eyes on Lincoln', value: 'R003 - Eyes on Lincoln'},
    {label: 'R004 - PV2 Dolphin', value: 'R004 - PV2 Dolphin'},
    {label: 'R005 - DE Mall of San Juan', value: 'R005 - DE Mall of San Juan'},
    {label: 'R006 - DE Brickell', value: 'R006 - DE Brickell'},
    {label: 'R008 - DE Florida Mall', value: 'R008 - DE Florida Mall'},
    {label: 'R009 - Aventura', value: 'R009 - Aventura'},
    {label: 'R010 - PV3 iDrive', value: 'R010 - PV3 iDrive'},
    {label: 'R011 - PV4 Vineland', value: 'R011 - PV4 Vineland'},
    {label: 'R014 - DE Plaza Las Americas', value: 'R014 - DE Plaza Las Americas'},
  ];

  const getFieldLabel = (field: string): string => {
    switch (field) {
      case 'fulfillment_status':
        return 'Status';
      case 'fulfillment_location':
        return 'Location';
      case 'decline_reason':
        return 'Decline Reason';
      default:
        return field;
    }
  };

  const getDisplayValue = (field: string, value: string): string => {
    if (!value) return 'Not set';
    
    switch (field) {
      case 'fulfillment_status':
        const statusOption = fulfillmentStatusOptions.find(opt => opt.value === value);
        return statusOption ? statusOption.label : value;
      case 'fulfillment_location':
        const locationOption = fulfillmentLocationOptions.find(opt => opt.value === value);
        return locationOption ? locationOption.label : value;
      default:
        return value;
    }
  };

  const generateChangesSummary = (): ChangesSummary[] => {
    const summary: ChangesSummary[] = [];
    
    Object.entries(lineItemChanges).forEach(([lineItemId, changes]) => {
      const lineItem = lineItems.find(item => item.line_item_id.toString() === lineItemId);
      if (!lineItem) return;

      const itemChanges: ChangesSummary['changes'] = [];
      
      Object.entries(changes).forEach(([field, newValue]) => {
        const oldValue = lineItem[field as keyof ShippingLineItem] as string;
        
        // Only include if there's an actual change
        if (oldValue !== newValue) {
          itemChanges.push({
            field,
            fieldLabel: getFieldLabel(field),
            oldValue: getDisplayValue(field, oldValue || ''),
            newValue: getDisplayValue(field, newValue || '')
          });
        }
      });

      if (itemChanges.length > 0) {
        summary.push({
          lineItemId,
          productName: lineItem.product_name,
          productSku: lineItem.product_sku,
          changes: itemChanges
        });
      }
    });

    return summary;
  };

  const handleSaveClick = () => {
    if (!order?.order_id || Object.keys(lineItemChanges).length === 0) {
      return;
    }

    const summary = generateChangesSummary();
    setChangesSummary(summary);
    setShowConfirmDialog(true);
  };

  const handleConfirmedSave = async () => {
    if (!order?.order_id || Object.keys(lineItemChanges).length === 0) {
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      // Transform the changes to API format
      const transformedData = transformLineItemChangesToApiFormat(
        lineItemChanges,
        lineItems,
        order.order_id
      );

      // Call the API
      const response = await updateLineItemsBulk(order.order_id, transformedData.line_item_updates);

      // Handle success
      if (response.success) {
        // Clear changes
        setLineItemChanges({});
        
        // Show success message
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Successfully updated ${response.data?.updated_count || 0} line items`,
          life: 3000
        });

        await refetch();
        reset();
        updateSingleOrder(order.order_id);

      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
      setSaveError(errorMessage);
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Save Failed',
        detail: errorMessage,
        life: 5000
      });
    } finally {
      setShowConfirmDialog(false);
      setSaving(false);
    }
  };

  const updateLineItem = (lineItemId: number, field: string, value: any) => {
    setLineItemChanges(prev => ({
      ...prev,
      [lineItemId]: {
        ...prev[lineItemId],
        [field]: value
      }
    }));
  };

  const getLineItemValue = (lineItem: ShippingLineItem, field: string) => {
    const changes = lineItemChanges[lineItem.line_item_id];
    if (changes && field in changes) {
      return changes[field as keyof typeof changes];
    }
    return lineItem[field as keyof ShippingLineItem];
  };

  const handleConfirmDialogHide = () => {
    setShowConfirmDialog(false);
  };

  const reset = () => {
    setLineItemChanges({});
    setSaving(false);
    setSaveError(null);
    setShowConfirmDialog(false);
    setChangesSummary([]);
  };

  const hasChanges = Object.keys(lineItemChanges).length > 0;

  return {
    // State
    lineItemChanges,
    saving,
    saveError,
    showConfirmDialog,
    changesSummary,
    toastRef,
    hasChanges,
    
    // Options
    fulfillmentStatusOptions,
    fulfillmentLocationOptions,
    
    // Handlers
    handleSaveClick,
    handleConfirmedSave,
    handleConfirmDialogHide,
    updateLineItem,
    getLineItemValue,
    reset,
    
    // Utility functions
    getFieldLabel,
    getDisplayValue
  };
};